import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar as MuiAppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Button,
  Avatar,
  styled,
  Paper,
  Alert,
  Collapse,
  Divider,
} from '@mui/material';
import Menu from '@mui/icons-material/Menu';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import People from '@mui/icons-material/People';
import FamilyRestroom from '@mui/icons-material/FamilyRestroom';
import GroupWork from '@mui/icons-material/GroupWork';
import AttachMoney from '@mui/icons-material/AttachMoney';
import Category from '@mui/icons-material/Category';
import School from '@mui/icons-material/School';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import Assignment from '@mui/icons-material/Assignment';
import Gavel from '@mui/icons-material/Gavel';
import Receipt from '@mui/icons-material/Receipt';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import Dashboard from '@mui/icons-material/Dashboard';
import Logout from '@mui/icons-material/Logout';
import Notifications from '@mui/icons-material/Notifications';
import Upload from '@mui/icons-material/Upload';
import Block from '@mui/icons-material/Block';
import Settings from '@mui/icons-material/Settings';
import Business from '@mui/icons-material/Business';
import Apartment from '@mui/icons-material/Apartment';
import PeopleAlt from '@mui/icons-material/PeopleAlt';
import AccountBalance from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import DomainIcon from '@mui/icons-material/Domain';
import ChatIcon from '@mui/icons-material/Chat';
import BuildIcon from '@mui/icons-material/Build';
import { useAuth } from '../../contexts/AuthContext';
import { CrudPermissions } from '../../types';
import { MetricsDashboard } from './MetricsDashboard';
import { StudentsManagement } from './StudentsManagement';
import { ParentsManagement } from './ParentsManagement';
import { CommunityMembersManagement } from './CommunityMembersManagement';
import { FeesManagement } from './FeesManagement';
import { FeeCategoriesManagement } from './FeeCategoriesManagement';
import { CoursesManagement } from './CoursesManagement';
import { SchoolYearsManagement } from './SchoolYearsManagement';
import { QuestionnairesManagement } from './QuestionnairesManagement';
import { FeeDisclaimersManagement } from './FeeDisclaimersManagement';
import { FeeAssessmentManagement } from './FeeAssessmentManagement';
import { OrdersManagement } from './OrdersManagement';
import { NotificationsManagement } from './NotificationsManagement';
import { NotificationRecipientsPage } from './NotificationRecipientsPage';
import { BulkImport } from './BulkImport';
import { AdminUsersManagement } from './AdminUsersManagement';
import { SchoolSettingsManagement } from './SchoolSettingsManagement';
import { BuildingsManagement } from './BuildingsManagement';
import { SchoolsManagement } from './SchoolsManagement';
import { WorkersManagement } from './WorkersManagement';
import Storefront from '@mui/icons-material/Storefront';

const drawerWidth = 300;
const miniDrawerWidth = 64;

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  borderRadius: 0,
}));

interface AdminDashboardProps {
  onLogout: () => void;
}

type AdminSection = 'metrics' | 'students' | 'parents' | 'community' | 'fees' | 'categories' | 'courses' | 'schoolyears' | 'questionnaires' | 'disclaimers' | 'feeassessment' | 'orders' | 'notifications' | 'buildings' | 'schools' | 'bulkimport' | 'adminusers' | 'schoolsettings' | 'workers';

type PermissionKey = 'students' | 'parents' | 'community' | 'fees' | 'feeAssessment' | 'orders' | 'categories' | 'courses' | 'schoolYears' | 'questionnaires' | 'disclaimers' | 'notifications' | 'buildings' | 'schools' | 'admins' | 'workers';

const sectionToPermissionKey: Record<AdminSection, PermissionKey | 'metrics' | 'bulkimport' | 'schoolsettings'> = {
  metrics: 'metrics',
  students: 'students',
  parents: 'parents',
  community: 'community',
  fees: 'fees',
  feeassessment: 'feeAssessment',
  orders: 'orders',
  categories: 'categories',
  courses: 'courses',
  schoolyears: 'schoolYears',
  questionnaires: 'questionnaires',
  disclaimers: 'disclaimers',
  notifications: 'notifications',
  buildings: 'buildings',
  schools: 'schools',
  bulkimport: 'bulkimport',
  adminusers: 'admins',
  schoolsettings: 'schoolsettings',
  workers: 'workers',
};

const noAccess: CrudPermissions = { create: false, read: false, update: false, delete: false };

const AccessDenied: React.FC<{ section: string }> = ({ section }) => (
  <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 500, mx: 'auto', mt: 8 }}>
    <Block sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
    <Typography variant="h5" gutterBottom fontWeight={600}>
      Access Denied
    </Typography>
    <Typography color="text.secondary" sx={{ mb: 3 }}>
      You do not have permission to access the {section} section.
    </Typography>
    <Alert severity="info">
      Contact your administrator if you believe you should have access to this area.
    </Alert>
  </Paper>
);

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout }) => {
  const { admin } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [currentSection, setCurrentSection] = useState<AdminSection>('metrics');
  const [viewingNotificationRecipients, setViewingNotificationRecipients] = useState<string | null>(null);
  const theme = useTheme();

  useEffect(() => {
    const handler = (e: Event) => {
      const section = (e as CustomEvent<AdminSection>).detail;
      if (section) setCurrentSection(section);
    };
    window.addEventListener('cc-admin-set-section', handler);
    return () => window.removeEventListener('cc-admin-set-section', handler);
  }, []);

  // Tour-driven sidebar choreography (step 12: Custom Questionnaires).
  // The tour cursor "clicks" the Communication & Forms group then the
  // Questionnaires sub-item; these triggers do the actual expand /
  // navigate since the cursor click is visual-only. Reset state only
  // when the step actually changes (detected via stepId), not when
  // other triggers within the same step fire — otherwise the
  // mid-step 'open-spirit-wear-edit' trigger would collapse the group
  // before the buyer is done watching the choreography.
  useEffect(() => {
    let ownerStepId: string | null = null;
    const onTrigger = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const stepId: string | undefined = detail?.stepId;
      if (detail?.trigger === 'expand-communication-menu') {
        ownerStepId = stepId ?? null;
        setExpandedGroups((prev) => ({ ...prev, communication: true }));
      } else if (detail?.trigger === 'navigate-to-questionnaires') {
        ownerStepId = stepId ?? null;
        setCurrentSection('questionnaires');
      } else if (ownerStepId !== null && stepId !== ownerStepId) {
        // The step that opened this group has moved on — collapse so
        // the next visit animates the expand again.
        ownerStepId = null;
        setExpandedGroups((prev) => (prev.communication ? { ...prev, communication: false } : prev));
      }
    };
    window.addEventListener('cc-tour-trigger', onTrigger);
    return () => window.removeEventListener('cc-tour-trigger', onTrigger);
  }, []);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const permissions = admin?.permissions;
  const isSuperAdmin = admin?.isSuperAdmin ?? false;

  const getPermissionForSection = (section: AdminSection): CrudPermissions => {
    if (isSuperAdmin) return { create: true, read: true, update: true, delete: true };
    if (!permissions) return noAccess;
    const key = sectionToPermissionKey[section];
    if (key === 'metrics') return permissions.canViewMetrics ? { create: false, read: true, update: false, delete: false } : noAccess;
    if (key === 'bulkimport') return permissions.canBulkImport ? { create: true, read: true, update: false, delete: false } : noAccess;
    if (key === 'schoolsettings') return noAccess;
    return permissions[key] ?? noAccess;
  };

  const canAccessSection = (section: AdminSection): boolean => {
    return getPermissionForSection(section).read;
  };

  interface MenuItem {
    id: AdminSection;
    label: string;
    icon: React.ReactNode;
  }

  interface MenuGroup {
    id: string;
    label: string;
    icon: React.ReactNode;
    items: MenuItem[];
  }

  const menuGroups: MenuGroup[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Dashboard />,
      items: [
        { id: 'metrics' as AdminSection, label: 'Metrics Dashboard', icon: <Dashboard /> },
      ],
    },
    {
      id: 'users',
      label: 'User Management',
      icon: <PeopleAlt />,
      items: [
        { id: 'students' as AdminSection, label: 'Students', icon: <People /> },
        { id: 'parents' as AdminSection, label: 'Parents', icon: <FamilyRestroom /> },
        { id: 'community' as AdminSection, label: 'Community Members', icon: <GroupWork /> },
        { id: 'workers' as AdminSection, label: 'POS Workers', icon: <Storefront /> },
      ],
    },
    {
      id: 'financial',
      label: 'Financial Management',
      icon: <AccountBalance />,
      items: [
        { id: 'fees' as AdminSection, label: 'Fees', icon: <AttachMoney /> },
        { id: 'feeassessment' as AdminSection, label: 'Fee Assessment', icon: <Receipt /> },
        { id: 'orders' as AdminSection, label: 'Orders', icon: <ShoppingCart /> },
        { id: 'categories' as AdminSection, label: 'Fee Categories', icon: <Category /> },
        { id: 'disclaimers' as AdminSection, label: 'Fee Disclaimers', icon: <Gavel /> },
      ],
    },
    {
      id: 'academic',
      label: 'Academic Management',
      icon: <SchoolIcon />,
      items: [
        { id: 'courses' as AdminSection, label: 'Courses', icon: <School /> },
        { id: 'schoolyears' as AdminSection, label: 'School Years', icon: <CalendarMonth /> },
      ],
    },
    {
      id: 'facilities',
      label: 'Facilities',
      icon: <DomainIcon />,
      items: [
        { id: 'schools' as AdminSection, label: 'Schools', icon: <Apartment /> },
        { id: 'buildings' as AdminSection, label: 'Buildings', icon: <Business /> },
      ],
    },
    {
      id: 'communication',
      label: 'Communication & Forms',
      icon: <ChatIcon />,
      items: [
        { id: 'notifications' as AdminSection, label: 'Notifications', icon: <Notifications /> },
        { id: 'questionnaires' as AdminSection, label: 'Questionnaires', icon: <Assignment /> },
      ],
    },
    {
      id: 'system',
      label: 'System Administration',
      icon: <BuildIcon />,
      items: [
        { id: 'adminusers' as AdminSection, label: 'Admin Users', icon: <AdminPanelSettings /> },
        { id: 'bulkimport' as AdminSection, label: 'Bulk Import', icon: <Upload /> },
        { id: 'schoolsettings' as AdminSection, label: 'School Settings', icon: <Settings /> },
      ],
    },
  ];

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    menuGroups.forEach(group => {
      initial[group.id] = false;
    });
    return initial;
  });

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const filteredMenuGroups = useMemo(() => {
    return menuGroups
      .map(group => ({
        ...group,
        items: group.items.filter(item => canAccessSection(item.id)),
      }))
      .filter(group => group.items.length > 0);
  }, [permissions, isSuperAdmin]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDrawerOpen(!drawerOpen);
    }
  };

  const handleSectionChange = (section: AdminSection) => {
    setCurrentSection(section);
    setViewingNotificationRecipients(null);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const renderContent = () => {
    const sectionPerm = getPermissionForSection(currentSection);
    if (!sectionPerm.read) {
      return <AccessDenied section={currentSection} />;
    }

    switch (currentSection) {
      case 'metrics':
        return <MetricsDashboard />;
      case 'bulkimport':
        return <BulkImport />;
      case 'students':
        return <StudentsManagement permissions={sectionPerm} />;
      case 'parents':
        return <ParentsManagement permissions={sectionPerm} />;
      case 'community':
        return <CommunityMembersManagement permissions={sectionPerm} />;
      case 'fees':
        return <FeesManagement permissions={sectionPerm} />;
      case 'feeassessment':
        return <FeeAssessmentManagement permissions={sectionPerm} />;
      case 'orders':
        return <OrdersManagement permissions={sectionPerm} />;
      case 'categories':
        return <FeeCategoriesManagement permissions={sectionPerm} />;
      case 'courses':
        return <CoursesManagement permissions={sectionPerm} />;
      case 'schoolyears':
        return <SchoolYearsManagement permissions={sectionPerm} />;
      case 'questionnaires':
        return <QuestionnairesManagement permissions={sectionPerm} />;
      case 'disclaimers':
        return <FeeDisclaimersManagement permissions={sectionPerm} />;
      case 'notifications':
        if (viewingNotificationRecipients) {
          return (
            <NotificationRecipientsPage
              notificationId={viewingNotificationRecipients}
              onBack={() => setViewingNotificationRecipients(null)}
            />
          );
        }
        return (
          <NotificationsManagement
            permissions={sectionPerm}
            onViewRecipients={(notificationId) => setViewingNotificationRecipients(notificationId)}
          />
        );
      case 'adminusers':
        return <AdminUsersManagement permissions={sectionPerm} />;
      case 'schoolsettings':
        return <SchoolSettingsManagement />;
      case 'buildings':
        return <BuildingsManagement permissions={sectionPerm} />;
      case 'schools':
        return <SchoolsManagement permissions={sectionPerm} />;
      case 'workers':
        return <WorkersManagement permissions={sectionPerm} />;
      default:
        return null;
    }
  };

  const drawer = (isOpen: boolean) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      <List sx={{ px: 1, py: 1, flex: 1 }}>
        {filteredMenuGroups.map((group, groupIndex) => (
          <Box key={group.id} data-tour-group={group.id}>
            {groupIndex > 0 && isOpen && <Divider sx={{ my: 1 }} />}
            <ListItemButton
              onClick={() => isOpen && toggleGroup(group.id)}
              data-tour={`admin-menu-group-${group.id}`}
              sx={{
                borderRadius: 1,
                py: 0.75,
                px: isOpen ? 1.5 : 1,
                mb: 0.5,
                justifyContent: isOpen ? 'flex-start' : 'center',
                bgcolor: 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: isOpen ? 32 : 'auto',
                  justifyContent: 'center',
                  color: 'text.secondary',
                }}
              >
                {group.icon}
              </ListItemIcon>
              {isOpen && (
                <>
                  <ListItemText
                    primary={group.label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  />
                  {expandedGroups[group.id] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </>
              )}
            </ListItemButton>
            <Collapse in={!isOpen || expandedGroups[group.id]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {group.items.map((item) => (
                  <ListItem key={item.id} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      selected={currentSection === item.id}
                      onClick={() => handleSectionChange(item.id)}
                      data-tour={`admin-menu-item-${item.id}`}
                      sx={{
                        borderRadius: 0,
                        justifyContent: isOpen ? 'flex-start' : 'center',
                        pl: isOpen ? 3.5 : 1.5,
                        pr: isOpen ? 2 : 1.5,
                        py: 0.75,
                        minHeight: 40,
                        borderLeft: '3px solid transparent',
                        '&.Mui-selected': {
                          bgcolor: 'rgba(25, 118, 210, 0.08)',
                          borderLeft: '3px solid',
                          borderLeftColor: 'primary.main',
                          color: 'primary.main',
                          '&:hover': {
                            bgcolor: 'rgba(25, 118, 210, 0.12)',
                          },
                        },
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: currentSection === item.id ? 'primary.main' : 'inherit',
                          minWidth: isOpen ? 36 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {isOpen && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </Box>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          width: '100%',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            {isMobile ? <Menu /> : drawerOpen ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
          <AdminPanelSettings sx={{ fontSize: 32, mr: 2 }} />
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Admin Portal
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  width: 36,
                  height: 36,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {admin?.firstName?.[0]}{admin?.lastName?.[0]}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {admin?.firstName} {admin?.lastName}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Administrator
                </Typography>
              </Box>
            </Box>
            <Button
              color="inherit"
              startIcon={<Logout />}
              onClick={onLogout}
              sx={{
                ml: 1,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flex: 1, mt: 8 }}>
        <Box
          component="nav"
          sx={{
            width: { xs: 0, md: drawerOpen ? drawerWidth : miniDrawerWidth },
            flexShrink: { md: 0 },
          }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                top: 64,
                height: 'calc(100% - 64px)',
                borderRadius: 0,
              },
            }}
          >
            {drawer(true)}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerOpen ? drawerWidth : miniDrawerWidth,
                top: 64,
                height: 'calc(100% - 64px)',
                transition: (theme) =>
                  theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                  }),
                overflowX: 'hidden',
                borderRadius: 0,
              },
            }}
            open
          >
            {drawer(drawerOpen)}
          </Drawer>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            bgcolor: '#f5f5f5',
            minHeight: 'calc(100vh - 64px)',
            minWidth: 0,
            overflowX: 'hidden',
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};
